import type { WorkspaceSearchItem } from '@/services/apiService'

export type WorkspaceOverviewSharingFilter = 'all' | 'personal' | 'shared'
export type WorkspaceOverviewOfflineFilter = 'all' | 'downloaded'
export type WorkspaceOverviewSortBy = 'name' | 'date' | 'modified'

export interface WorkspaceOverviewResult {
  clientId: string
  type: 'workspace'
  title: string
  description?: string
  isPublic: boolean
  isOwner: boolean
  userRole: string | null
  canEdit: boolean
  hasWorkspaceRole: boolean
  isBookmarked: boolean
  isBookmarkOnly: boolean
  ownerNames: string[] | null
  tourCount: number
  poiCount: number
  photoCount: number
  workspaceDate?: number
  sortableDate: number
  createdAt: string | null
  lastModifiedAt: string | null
  activityStart: string | null
  activityEnd: string | null
  coverPhotoThumbnailUrl?: string | null
  isPinned: boolean | null
  isDownloaded: boolean
  hasActivityDate: boolean
}

const getWorkspaceDate = (workspace: WorkspaceSearchItem) => {
  const allTracks = workspace.tours.flatMap((tour) => tour.tracks)
  const tracksWithTime = allTracks.filter((track) => track.startTime)
  const timestamps = tracksWithTime
    .map((track) => new Date(track.startTime!).getTime())
    .filter((ts) => !isNaN(ts))

  if (timestamps.length > 0) {
    return Math.max(...timestamps)
  }
  return undefined
}

export const buildWorkspaceOverviewResults = (
  workspaces: WorkspaceSearchItem[],
  options: {
    query: string
    sortBy: WorkspaceOverviewSortBy
    sortDirection: 'asc' | 'desc'
  },
): WorkspaceOverviewResult[] => {
  const query = options.query.toLowerCase().trim()
  const results: WorkspaceOverviewResult[] = []

  workspaces.forEach((workspace) => {
    if (
      query &&
      !workspace.name.toLowerCase().includes(query) &&
      !workspace.description?.toLowerCase().includes(query)
    ) {
      return
    }

    const workspaceDate = getWorkspaceDate(workspace)
    const createdAt = workspace.createdAt ?? null
    const createdTs = createdAt ? new Date(createdAt).getTime() : 0
    const userRole = workspace.userRole ?? null
    const hasWorkspaceRole = userRole !== null || workspace.isOwner || workspace.canEdit === true
    const isBookmarked = workspace.isBookmarked === true

    results.push({
      clientId: workspace.clientId,
      type: 'workspace',
      title: workspace.name,
      description: workspace.description || undefined,
      isPublic: workspace.isPublic,
      isOwner: workspace.isOwner,
      userRole,
      canEdit: workspace.canEdit === true,
      hasWorkspaceRole,
      isBookmarked,
      isBookmarkOnly: isBookmarked && !hasWorkspaceRole,
      ownerNames: workspace.ownerNames ?? null,
      tourCount: workspace.tours.length,
      poiCount: workspace.poiCount ?? 0,
      photoCount: workspace.photoCount ?? 0,
      workspaceDate,
      sortableDate: workspaceDate ?? createdTs,
      createdAt,
      lastModifiedAt: workspace.lastModifiedAt ?? null,
      activityStart: workspace.activityStart ?? null,
      activityEnd: workspace.activityEnd ?? null,
      coverPhotoThumbnailUrl: workspace.coverPhotoThumbnailUrl ?? null,
      isPinned: workspace.isPinned ?? null,
      isDownloaded: Boolean(
        (workspace as WorkspaceSearchItem & { isDownloaded?: boolean }).isDownloaded,
      ),
      hasActivityDate: workspaceDate !== undefined,
    })
  })

  return results.sort((a, b) => {
    let comparison = 0

    switch (options.sortBy) {
      case 'name':
        comparison = a.title.localeCompare(b.title)
        break
      case 'modified': {
        const aTs = a.lastModifiedAt ? new Date(a.lastModifiedAt).getTime() : 0
        const bTs = b.lastModifiedAt ? new Date(b.lastModifiedAt).getTime() : 0
        comparison = aTs - bTs
        break
      }
      case 'date':
        if (options.sortDirection === 'desc' && a.hasActivityDate !== b.hasActivityDate) {
          comparison = Number(a.hasActivityDate) - Number(b.hasActivityDate)
          break
        }
        comparison = a.sortableDate - b.sortableDate
        break
    }

    return options.sortDirection === 'asc' ? comparison : -comparison
  })
}

export const applyWorkspaceOverviewFilters = (
  results: WorkspaceOverviewResult[],
  options: {
    sharingFilter: WorkspaceOverviewSharingFilter
    offlineFilter: WorkspaceOverviewOfflineFilter
    isTauriApp: boolean
  },
): WorkspaceOverviewResult[] => {
  let filteredResults = results

  if (options.sharingFilter === 'personal') {
    filteredResults = filteredResults.filter((result) => !result.isPublic && result.isOwner)
  } else if (options.sharingFilter === 'shared') {
    filteredResults = filteredResults.filter(
      (result) =>
        result.isPublic || !result.isOwner || result.isBookmarkOnly || result.isBookmarked,
    )
  }

  if (options.isTauriApp && options.offlineFilter === 'downloaded') {
    filteredResults = filteredResults.filter((result) => result.isDownloaded)
  }

  return filteredResults
}
